/* Cinderglow – landing behaviour: scroll reveal, header state, ember canvas, card glow, gallery thumbs, quantity, sticky bar. */
(() => {
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Scroll reveal. Content is only hidden once this script runs (html.cg-anim), so it stays visible without JS.
  const reveals = document.querySelectorAll('.cg [data-reveal]');
  const show = (el) => el.classList.add('is-in');
  if (reduce || !('IntersectionObserver' in window)) {
    reveals.forEach(show);
  } else {
    document.documentElement.classList.add('cg-anim');
    const io = new IntersectionObserver(
      (entries) =>
        entries.forEach((e) => {
          if (e.isIntersecting) {
            show(e.target);
            io.unobserve(e.target);
          }
        }),
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );
    reveals.forEach((el) => {
      // Anything already on screen is shown right away, without waiting for the observer.
      if (el.getBoundingClientRect().top < window.innerHeight) setTimeout(() => show(el), 40);
      else io.observe(el);
    });
    // Safety net: if the observer is late (anchor jumps, background tabs), reveal on scroll by position.
    let ticking = false;
    window.addEventListener('scroll', () => {
      if (ticking) return;
      ticking = true;
      setTimeout(() => {
        ticking = false;
        document.querySelectorAll('.cg [data-reveal]:not(.is-in)').forEach((el) => {
          if (el.getBoundingClientRect().top < window.innerHeight * 0.95) show(el);
        });
      }, 120);
    }, { passive: true });
  }

  // Header border once the page is scrolled
  const header = document.querySelector('.cg-header');
  if (header) {
    const onScroll = () => header.classList.toggle('is-stuck', window.scrollY > 12);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  // Cursor-following glow on cards
  document.querySelectorAll('.cg-card').forEach((card) => {
    card.addEventListener('pointermove', (e) => {
      const r = card.getBoundingClientRect();
      card.style.setProperty('--mx', `${e.clientX - r.left}px`);
      card.style.setProperty('--my', `${e.clientY - r.top}px`);
    });
  });

  // Gallery thumbnails in the buy block
  document.querySelectorAll('[data-cg-gallery]').forEach((root) => {
    const main = root.querySelector('[data-cg-main]');
    const video = root.querySelector('[data-cg-video]');
    root.querySelectorAll('[data-cg-thumb]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const isVideo = 'video' in btn.dataset;
        if (video) {
          video.hidden = !isVideo;
          if (isVideo) video.play().catch(() => {}); else video.pause();
        }
        if (main) main.hidden = isVideo && !!video;
        root.querySelectorAll('[data-cg-thumb]').forEach((b) => b.setAttribute('aria-current', String(b === btn)));
        if (isVideo || !main) return;
        main.src = btn.dataset.src;
        main.srcset = btn.dataset.srcset || '';
        main.alt = btn.dataset.alt || '';
        root.querySelectorAll('[data-cg-thumb]').forEach((b) => b.setAttribute('aria-current', String(b === btn)));
      });
    });
  });

  // Quantity stepper
  document.querySelectorAll('[data-cg-qty]').forEach((box) => {
    const input = box.querySelector('input');
    box.addEventListener('click', (e) => {
      const step = Number(e.target.closest('button')?.dataset.step || 0);
      if (!step) return;
      input.value = Math.min(10, Math.max(1, (parseInt(input.value, 10) || 1) + step));
    });
  });

  // Sets with scent oils: show as many scent pickers as the chosen set needs, then add everything in one request.
  document.querySelectorAll('[data-cg-bundle]').forEach((root) => {
    const form = root.matches('form') ? root : root.querySelector('form');
    const picksBox = root.querySelector('[data-cg-picks]');
    const wraps = [...root.querySelectorAll('[data-cg-pick-wrap]')];
    const error = root.querySelector('[data-cg-error]');
    const count = () => Number(root.querySelector('[data-cg-count]:checked')?.value || 0);
    const sync = () => {
      const n = count();
      if (picksBox) picksBox.hidden = n === 0;
      wraps.forEach((w, i) => { w.hidden = i >= n; });
    };
    root.addEventListener('change', (e) => { if (e.target.matches('[data-cg-count]')) sync(); });
    sync();
    if (!form) return;
    form.addEventListener('submit', async (e) => {
      const n = count();
      if (n === 0) return; // plain product form, no scents
      e.preventDefault();
      const items = [];
      const base = form.querySelector('input[type="hidden"][name="id"]');
      if (base) items.push({ id: Number(base.value), quantity: Math.max(1, parseInt(form.querySelector('[name="quantity"]')?.value, 10) || 1) });
      const perScent = {};
      wraps.slice(0, n).forEach((w) => { const id = w.querySelector('select').value; perScent[id] = (perScent[id] || 0) + 1; });
      Object.entries(perScent).forEach(([id, quantity]) => items.push({ id: Number(id), quantity }));
      const btn = form.querySelector('[type="submit"]');
      btn.disabled = true;
      if (error) error.hidden = true;
      try {
        const root_url = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
        const res = await fetch(`${root_url}cart/add.js`, { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify({ items }) });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).description || 'Could not add to cart. Please try again.');
        window.location.href = root.dataset.next || '/cart';
      } catch (err) {
        if (error) { error.textContent = err.message; error.hidden = false; }
        btn.disabled = false;
      }
    });
  });

  // Sticky buy bar: visible after the hero, hidden while the buy block itself is on screen
  const sticky = document.querySelector('.cg-sticky');
  const hero = document.querySelector('.cg-hero');
  const buy = document.querySelector('#buy');
  if (sticky && hero && 'IntersectionObserver' in window) {
    let pastHero = false;
    let atBuy = false;
    const update = () => sticky.classList.toggle('is-on', pastHero && !atBuy);
    new IntersectionObserver(([e]) => { pastHero = !e.isIntersecting && e.boundingClientRect.top < 0; update(); }).observe(hero);
    if (buy) new IntersectionObserver(([e]) => { atBuy = e.isIntersecting; update(); }, { threshold: 0.2 }).observe(buy);
  }

  // Ember particles in the hero
  const canvas = document.querySelector('.cg-hero__embers');
  if (!canvas || reduce) return;
  const ctx = canvas.getContext('2d');
  let w = 0, h = 0, raf = 0, running = false;
  const dpr = Math.min(window.devicePixelRatio || 1, 2);
  const embers = [];
  const spawn = (fromBottom) => ({
    x: Math.random() * w,
    y: fromBottom ? h + Math.random() * 40 : Math.random() * h,
    r: 0.6 + Math.random() * 1.9,
    vy: 0.25 + Math.random() * 0.75,
    drift: (Math.random() - 0.5) * 0.5,
    phase: Math.random() * Math.PI * 2,
    life: 0.35 + Math.random() * 0.65,
  });
  const resize = () => {
    const rect = canvas.getBoundingClientRect();
    w = rect.width; h = rect.height;
    canvas.width = w * dpr; canvas.height = h * dpr;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const count = Math.round(Math.min(90, Math.max(28, w / 16)));
    embers.length = 0;
    for (let i = 0; i < count; i++) embers.push(spawn(false));
  };
  const tick = () => {
    ctx.clearRect(0, 0, w, h);
    ctx.globalCompositeOperation = 'lighter';
    for (const p of embers) {
      p.y -= p.vy; p.phase += 0.02; p.x += p.drift + Math.sin(p.phase) * 0.35;
      const fade = Math.min(1, Math.max(0, p.y / h)) * p.life;
      if (p.y < -10 || fade <= 0.01) Object.assign(p, spawn(true));
      const g = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.r * 5);
      g.addColorStop(0, `rgba(255, 190, 110, ${0.9 * fade})`);
      g.addColorStop(0.3, `rgba(255, 110, 30, ${0.45 * fade})`);
      g.addColorStop(1, 'rgba(255, 90, 10, 0)');
      ctx.fillStyle = g;
      ctx.beginPath(); ctx.arc(p.x, p.y, p.r * 5, 0, Math.PI * 2); ctx.fill();
    }
    if (running) raf = requestAnimationFrame(tick);
  };
  resize();
  window.addEventListener('resize', resize, { passive: true });
  new IntersectionObserver(([e]) => {
    running = e.isIntersecting && !document.hidden;
    cancelAnimationFrame(raf);
    if (running) raf = requestAnimationFrame(tick);
  }).observe(canvas);
  document.addEventListener('visibilitychange', () => {
    if (document.hidden) { running = false; cancelAnimationFrame(raf); }
  });
})();
